import { useEffect, useMemo, useState } from "react";
import { findAlternatives, type AlternativeSuggestion } from "@jilian/core";
import { EXERCISE_BY_ID, EXERCISE_LIBRARY } from "../data/exercises";
import { useSettings } from "../db/hooks";
import { loadLatestWeights } from "../db/workoutRepo";
import { useSessionStore } from "../state/useSessionStore";
import { Button, RiskTag, Sheet } from "./ui";

export function ReplacementSheet({
  fromExerciseId,
  open,
  onClose,
}: {
  fromExerciseId: string;
  open: boolean;
  onClose: () => void;
}) {
  const exercises = useSessionStore((s) => s.exercises);
  const replaceExercise = useSessionStore((s) => s.replaceExercise);
  const settings = useSettings();
  const [history, setHistory] = useState<Map<string, { weightKg: number; weightUnitScope: import("@jilian/shared").WeightUnitScope }>>(new Map());

  const from = EXERCISE_BY_ID.get(fromExerciseId);
  const used = useMemo(() => new Set(exercises.map((e) => e.exerciseId)), [exercises]);

  // 历史重量（最近一次使用重量；供「上次重量」展示）
  useEffect(() => {
    let mounted = true;
    if (!open) return;
    void loadLatestWeights(settings?.profile.id ?? "user-demo").then((m) => {
      if (mounted) setHistory(m);
    });
    return () => {
      mounted = false;
    };
  }, [open, settings]);

  // 阶段 3：替代候选由 @jilian/core findAlternatives 计算（§16 筛选顺序 + 硬锁 vs 相对风险）。
  // 喜欢/厌恶偏好表待阶段 4 建库（当前为空集合）。
  const alternatives: AlternativeSuggestion[] = useMemo(() => {
    if (!from || !settings) return [];
    return findAlternatives({
      from,
      injury: settings.injury,
      alreadyUsed: used,
      library: EXERCISE_LIBRARY,
      likedExerciseIds: new Set(),
      dislikedExerciseIds: new Set(),
      history,
    });
  }, [from, settings, used, history]);

  if (!from) return null;

  return (
    <Sheet open={open} onClose={onClose} title={`替换「${from.nameZh}」`}>
      <p className="text-[13px] text-tone-soft mb-3">
        仅替换当前动作；新动作使用自己的重量与历史（阶段 3：由规则引擎筛选并给出风险标签）。
      </p>
      <div className="space-y-2">
        {alternatives.map((alt) => (
          <div
            key={alt.exercise.id}
            className={`rounded-xl border p-3 ${
              alt.blocker === "hard_lock"
                ? "border-tone-danger/40 bg-tone-danger/5"
                : "border-tone-border bg-tone-card"
            }`}
          >
            <div className="flex items-center justify-between gap-2">
              <div>
                <div className="font-semibold text-tone-ink">{alt.exercise.nameZh}</div>
                <div className="text-[12px] text-tone-soft">
                  {alt.exercise.primaryMuscles.join(" · ")} · 前足负荷 {alt.exercise.forefootLoad}
                  {alt.hasHistory && alt.previousWeightKg != null
                    ? ` · 上次 ${alt.previousWeightKg} kg${alt.weightUnitScope === "per_dumbbell" ? "/只" : ""}`
                    : " · 首次校准"}
                </div>
              </div>
              {alt.blocker === "hard_lock" ? (
                <RiskTag tone="danger">医疗锁</RiskTag>
              ) : alt.blocker === "relative_risk_up" ? (
                <RiskTag tone="warn">风险升高</RiskTag>
              ) : (
                <RiskTag tone="good">可用</RiskTag>
              )}
            </div>
            {alt.riskNote ? <p className="text-[12px] text-tone-soft mt-1">{alt.riskNote}</p> : null}
            <div className="text-[12px] text-tone-soft mt-1">推荐依据：{alt.reasonCodes.join(" / ")}</div>
            <Button
              variant={alt.blocker === "hard_lock" ? "soft" : "secondary"}
              className="w-full mt-2"
              disabled={alt.blocker === "hard_lock"}
              onClick={() => {
                replaceExercise(fromExerciseId, alt.exercise.id);
                onClose();
              }}
            >
              {alt.blocker === "hard_lock" ? "医疗许可前锁定" : "选择此动作"}
            </Button>
          </div>
        ))}
        {alternatives.length === 0 ? (
          <div className="text-center text-tone-soft text-[14px] py-8">动作库暂无合适的替代动作。</div>
        ) : null}
      </div>
    </Sheet>
  );
}